import React, { useEffect, useLayoutEffect, useRef } from "react";
import SchoolListSection from "../sidebar/SchoolListSection";
import { SCHOOL_LIST_TIPS } from "../sidebar/schoolListTips";
import { UI_MSG } from "../../config/uiMessages";

/** ~2 school rows visible; list scrolls for the rest. */
const MOBILE_CARD_LIST_SCROLL_PX = 134;
const LIST_INITIAL = 5;
const LIST_STEP = 5;
const LIST_MAX = 20;
const OPEN_LIST_MAX = 50;

function bucketIndexForSchool(
  id,
  topItems,
  middleItems,
  nearbyItems,
  bottomItems
) {
  if (id == null) return null;
  if (topItems.some((s) => s.id === id)) return 0;
  if (middleItems.some((s) => s.id === id)) return 1;
  if (nearbyItems.some((s) => s.id === id)) return 2;
  if (bottomItems.some((s) => s.id === id)) return 3;
  return null;
}

function titleWithCount(label, count) {
  return (
    <>
      {label}{" "}
      <span className="v2-section-title-count">({count})</span>
    </>
  );
}

function rowEsc(id) {
  return typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(String(id))
    : String(id);
}

/** Scroll list so the row wrapper sits at the top (transport panel opens below in-card). */
function scrollListSoRowAtTop(listEl, rowEl) {
  if (!listEl || !rowEl) return;
  const listRect = listEl.getBoundingClientRect();
  const rowRect = rowEl.getBoundingClientRect();
  const nextTop = rowRect.top - listRect.top + listEl.scrollTop;
  listEl.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
}

/**
 * Phone-only: horizontal swipe between the four school buckets (same as left column on desktop).
 */
export default function MobileSchoolCardsDeck({
  topItems,
  middleItems,
  nearbyItems = [],
  bottomItems,
  selectedIds,
  onToggleSchool,
  homeLocation,
  transportSchool,
  onOpenTransport,
  onCloseTransport,
  onSelectRoute,
  onHoverRoute,
  onLeaveRoute,
  onLeaveOptionsList,
  onClearRoute,
  onOpenSchoolDetails,
  onSchoolRowClick,
  drawerSchoolId,
  focusedSchoolId,
  catchmentCheckBySchoolId,
  catchmentLoading,
  /** Resets horizontal + vertical scroll when home moves */
  homeScrollKey,
  onClearAllCatchments,
  isFavourite,
  onToggleFavourite,
  favouritesFilterOn = false,
  favouriteCount = 0,
  schoolsError = null,
  catchmentCheckError = null,
}) {
  const railRef = useRef(null);
  const cardRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const listRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const prevHomeScrollKeyRef = useRef(null);
  /** After home moves, stay on “Schools in Catchment” (rail start); skip one focus-driven rail/list snap */
  const skipFocusAlignForHomeChangeRef = useRef(false);

  const limitedListProps = {
    initialVisible: LIST_INITIAL,
    loadStep: LIST_STEP,
    maxItems: LIST_MAX,
    showLoadMore: true,
    scrollHeight: MOBILE_CARD_LIST_SCROLL_PX,
    resetKey: homeScrollKey,
  };

  const favEmpty =
    favouriteCount === 0 ? UI_MSG.FAV_EMPTY_NONE_YET : UI_MSG.FAV_EMPTY_SECTION;
  const emptyOrFav = (fallback) => {
    if (schoolsError) return UI_MSG.SCHOOLS_LOAD_FAILED;
    return favouritesFilterOn ? favEmpty : fallback;
  };
  const favRowProps = { isFavourite, onToggleFavourite };

  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const homeChanged = prevHomeScrollKeyRef.current !== homeScrollKey;
    prevHomeScrollKeyRef.current = homeScrollKey;
    if (!homeChanged) return;

    skipFocusAlignForHomeChangeRef.current = true;
    rail.scrollLeft = 0;
    listRefs.forEach((r) => {
      if (r.current) r.current.scrollTop = 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listRefs stable
  }, [homeScrollKey]);

  useEffect(() => {
    const idx = bucketIndexForSchool(
      focusedSchoolId,
      topItems,
      middleItems,
      nearbyItems,
      bottomItems
    );
    if (idx == null) return;

    if (skipFocusAlignForHomeChangeRef.current) {
      skipFocusAlignForHomeChangeRef.current = false;
      return;
    }

    const rail = railRef.current;
    const card = cardRefs[idx].current;
    const listEl = listRefs[idx].current;

    if (rail && card) {
      const targetLeft = card.offsetLeft - (rail.clientWidth - card.clientWidth) / 2;
      rail.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: "smooth",
      });
    }

    if (listEl && focusedSchoolId != null) {
      const row = listEl.querySelector(
        `[data-school-row-id="${rowEsc(focusedSchoolId)}"]`
      );
      row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cardRefs, listRefs stable
  }, [focusedSchoolId, topItems, middleItems, nearbyItems, bottomItems]);

  useLayoutEffect(() => {
    const id = transportSchool?.id;
    if (id == null) return;

    const idx = bucketIndexForSchool(
      id,
      topItems,
      middleItems,
      nearbyItems,
      bottomItems
    );
    if (idx == null) return;

    const apply = () => {
      const rail = railRef.current;
      const card = cardRefs[idx].current;
      const listEl = listRefs[idx].current;
      if (rail && card) {
        const targetLeft =
          card.offsetLeft - (rail.clientWidth - card.clientWidth) / 2;
        rail.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: "smooth",
        });
      }
      if (!listEl) return;
      const row = listEl.querySelector(`[data-school-row-id="${rowEsc(id)}"]`);
      scrollListSoRowAtTop(listEl, row);
    };

    apply();
    const t = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cardRefs, listRefs stable
  }, [transportSchool?.id, topItems, middleItems, nearbyItems, bottomItems]);

  return (
    <section className="v2-mobile-cards-deck" aria-label="School lists">
      {schoolsError ? (
        <div className="v2-inline-alert v2-inline-alert--mobile" role="alert">
          {schoolsError}
        </div>
      ) : null}
      {catchmentCheckError ? (
        <div className="v2-inline-alert v2-inline-alert--mobile" role="alert">
          {UI_MSG.CATCHMENT_CHECK_FAILED}
        </div>
      ) : null}
      <div ref={railRef} className="v2-mobile-cards-rail">
        <div ref={cardRefs[0]} className="v2-mobile-card v2-mobile-card--snap">
          <SchoolListSection
            title={titleWithCount("Schools In Catchment", topItems.length)}
            titleTip={SCHOOL_LIST_TIPS.inCatchment}
            sectionAccent="green"
            items={topItems}
            selectedIds={selectedIds}
            onToggle={onToggleSchool}
            onClearAllCatchments={onClearAllCatchments}
            homeLocation={homeLocation}
            transportSchool={transportSchool}
            onOpenTransport={onOpenTransport}
            onCloseTransport={onCloseTransport}
            onSelectRoute={onSelectRoute}
            onHoverRoute={onHoverRoute}
            onLeaveRoute={onLeaveRoute}
            onLeaveOptionsList={onLeaveOptionsList}
            onClearRoute={onClearRoute}
            onOpenDetails={onOpenSchoolDetails}
            onRowClick={onSchoolRowClick}
            drawerSchoolId={drawerSchoolId}
            focusedSchoolId={focusedSchoolId}
            showCatchmentHover
            catchmentCheckBySchoolId={catchmentCheckBySchoolId}
            catchmentLoading={catchmentLoading}
            emptyText={emptyOrFav("No schools found in catchment for this home.")}
            showLoadMore={false}
            scrollHeight={MOBILE_CARD_LIST_SCROLL_PX}
            compactMobileCard
            listRef={listRefs[0]}
            {...favRowProps}
          />
        </div>

        <div ref={cardRefs[1]} className="v2-mobile-card v2-mobile-card--snap">
          <SchoolListSection
            title={titleWithCount(
              "Schools Open To All",
              middleItems.length
            )}
            titleTip={SCHOOL_LIST_TIPS.openToAll}
            sectionAccent="slate"
            items={middleItems}
            selectedIds={selectedIds}
            onToggle={onToggleSchool}
            onClearAllCatchments={onClearAllCatchments}
            homeLocation={homeLocation}
            transportSchool={transportSchool}
            onOpenTransport={onOpenTransport}
            onCloseTransport={onCloseTransport}
            onSelectRoute={onSelectRoute}
            onHoverRoute={onHoverRoute}
            onLeaveRoute={onLeaveRoute}
            onLeaveOptionsList={onLeaveOptionsList}
            onClearRoute={onClearRoute}
            onOpenDetails={onOpenSchoolDetails}
            onRowClick={onSchoolRowClick}
            drawerSchoolId={drawerSchoolId}
            focusedSchoolId={focusedSchoolId}
            emptyText={emptyOrFav("No open-catchment schools to show.")}
            showOpenSeatsBracket
            {...limitedListProps}
            maxItems={OPEN_LIST_MAX}
            compactMobileCard
            listRef={listRefs[1]}
            {...favRowProps}
          />
        </div>

        <div ref={cardRefs[2]} className="v2-mobile-card v2-mobile-card--snap">
          <SchoolListSection
            title={titleWithCount(
              "Possible Catchment",
              nearbyItems.length
            )}
            titleTip={SCHOOL_LIST_TIPS.possible}
            sectionAccent="blue"
            items={nearbyItems}
            selectedIds={selectedIds}
            onToggle={onToggleSchool}
            onClearAllCatchments={onClearAllCatchments}
            homeLocation={homeLocation}
            transportSchool={transportSchool}
            onOpenTransport={onOpenTransport}
            onCloseTransport={onCloseTransport}
            onSelectRoute={onSelectRoute}
            onHoverRoute={onHoverRoute}
            onLeaveRoute={onLeaveRoute}
            onLeaveOptionsList={onLeaveOptionsList}
            onClearRoute={onClearRoute}
            onOpenDetails={onOpenSchoolDetails}
            onRowClick={onSchoolRowClick}
            drawerSchoolId={drawerSchoolId}
            focusedSchoolId={focusedSchoolId}
            emptyText={emptyOrFav("No schools within ~8 miles with possible / unconfirmed catchment.")}
            {...limitedListProps}
            compactMobileCard
            listRef={listRefs[2]}
            {...favRowProps}
          />
        </div>

        <div ref={cardRefs[3]} className="v2-mobile-card v2-mobile-card--snap">
          <SchoolListSection
            title={titleWithCount("Schools Outside Catchment", bottomItems.length)}
            titleTip={SCHOOL_LIST_TIPS.outside}
            sectionAccent="yellow"
            items={bottomItems}
            selectedIds={selectedIds}
            onToggle={onToggleSchool}
            onClearAllCatchments={onClearAllCatchments}
            homeLocation={homeLocation}
            transportSchool={transportSchool}
            onOpenTransport={onOpenTransport}
            onCloseTransport={onCloseTransport}
            onSelectRoute={onSelectRoute}
            onHoverRoute={onHoverRoute}
            onLeaveRoute={onLeaveRoute}
            onLeaveOptionsList={onLeaveOptionsList}
            onClearRoute={onClearRoute}
            onOpenDetails={onOpenSchoolDetails}
            onRowClick={onSchoolRowClick}
            drawerSchoolId={drawerSchoolId}
            focusedSchoolId={focusedSchoolId}
            emptyText={emptyOrFav("No schools outside catchment to show.")}
            {...limitedListProps}
            compactMobileCard
            listRef={listRefs[3]}
            {...favRowProps}
          />
        </div>
      </div>
    </section>
  );
}
